import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useTeacherProfile } from '../../../contexts/TeacherProfileContext';
import { studentService } from '../../../services/studentService';
import { studentAttendanceService, AttendanceData, DownloadReportRequest } from '../../../services/studentAttendanceService';
import { authService } from '../../../services/authService';
import { Student } from '../../../models';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, Download, CheckCircle, XCircle } from 'lucide-react';
import {
  TeacherPageShell,
  TeacherPageHeader,
  TeacherHeaderActions,
  TeacherButton,
  TeacherStatsGrid,
  TeacherStatCard,
  TeacherPanel,
} from '../shared';

interface AttendanceRecord {
  studentId: string;
  studentName: string;
  rollNo: string | number;
  email: string;
  className: string;
  status: 'present' | 'absent';
}

interface AttendanceDataState {
  date: string;
  records: AttendanceRecord[];
  totalPresent: number;
  totalAbsent: number;
}

// Shimmer Loading Components
const ShimmerCard: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="bg-gray-300 dark:bg-gray-700 rounded-xl h-full"></div>
  </div>
);

const ShimmerTableRow: React.FC = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-8"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="h-10 w-10 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        <div className="ml-4">
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-24"></div>
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-32"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-16"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-center">
      <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded-full w-20 mx-auto"></div>
    </td>
  </tr>
);

// Custom Calendar Component
const CustomCalendar: React.FC<{
  selectedDate: string;
  onDateSelect: (date: string) => void;
  onClose: () => void;
  isDarkMode: boolean;
  markedDates: string[];
}> = ({ selectedDate, onDateSelect, onClose, isDarkMode, markedDates }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const today = new Date();
  const selectedDateObj = new Date(selectedDate);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateClick = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    onDateSelect(dateString);
    onClose();
  };

  const isToday = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return dateString === today.toISOString().split('T')[0];
  };

  const isSelected = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return dateString === selectedDate;
  };

  const isHovered = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return dateString === hoveredDate;
  };

  const isMarked = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return markedDates.includes(dateString);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className={`rounded-2xl p-6 w-full max-w-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-2xl`}>
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigateMonth('prev')}
          className={`p-2 rounded-lg transition-colors duration-200 ${
            isDarkMode 
              ? 'hover:bg-gray-700 text-gray-300' 
              : 'hover:bg-gray-100 text-gray-600'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={() => navigateMonth('next')}
          className={`p-2 rounded-lg transition-colors duration-200 ${
            isDarkMode 
              ? 'hover:bg-gray-700 text-gray-300' 
              : 'hover:bg-gray-100 text-gray-600'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day Names Header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (day === null) {
            return <div key={index} className="h-10" />;
          }

          const isCurrentDay = isToday(day);
          const isSelectedDay = isSelected(day);
          const isHoveredDay = isHovered(day);
          const isMarkedDay = isMarked(day);

          return (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              onMouseEnter={() => {
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth() + 1;
                const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                setHoveredDate(dateString);
              }}
              onMouseLeave={() => setHoveredDate(null)}
              className={`h-10 w-10 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                isSelectedDay
                  ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                  : isCurrentDay
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : isHoveredDay
                  ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {day}
              {isMarkedDay && !isSelectedDay && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex space-x-2">
          <button
            onClick={() => {
              const todayString = today.toISOString().split('T')[0];
              onDateSelect(todayString);
              onClose();
            }}
            className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
          >
            Today
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const StudentAttendance: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuth();
  const { teacherProfile } = useTeacherProfile();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState<AttendanceDataState | null>(null);
  const [markedDates, setMarkedDates] = useState<string[]>([]);
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

  // Load initial data (students and marked dates)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        if (user?.schoolId && user?.classId) {
          // Load students and marked dates in parallel
          const [studentsResponse, markedDatesResponse] = await Promise.all([
            studentService.getStudentsByClass(user.schoolId, user.classId),
            studentAttendanceService.getMarkedDates(user.schoolId)
          ]);

          // Handle students response
          if (Array.isArray(studentsResponse)) {
            setStudents(studentsResponse);
          } else if (studentsResponse.success && Array.isArray(studentsResponse.students)) {
            setStudents(studentsResponse.students);
          } else {
            console.error('Unexpected students response format:', studentsResponse);
            toast.error('Failed to load students');
          }

          // Handle marked dates response
          setMarkedDates(markedDatesResponse.markedDates);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [user?.schoolId, user?.classId]);

  // Load attendance for selected date
  useEffect(() => {
    if (selectedDate && students.length > 0) {
      loadAttendanceForDate(selectedDate);
    }
  }, [selectedDate, students]);

  const loadAttendanceForDate = async (date: string) => {
    try {
      if (!user?.schoolId) return;

      setIsLoadingAttendance(true);

      // Try to fetch existing attendance data
      const existingAttendance = await studentAttendanceService.getAttendanceByDate(user.schoolId, date);
      
      // Create attendance records from student data
      const attendanceRecords: AttendanceRecord[] = students.map(student => {
        // Check if attendance already exists for this student
        const existingRecord = existingAttendance.find(att => att.studentId === student.studentId);
        
        return {
          studentId: student.studentId,
          studentName: student.name,
          rollNo: student.rollNo ? String(student.rollNo) : '',
          email: student.email || '',
          className: student.className || '',
          status: existingRecord ? (existingRecord.isPresent ? 'present' : 'absent') : 'present'
        };
      });

      setAttendanceData({
        date,
        records: attendanceRecords,
        totalPresent: attendanceRecords.filter(r => r.status === 'present').length,
        totalAbsent: attendanceRecords.filter(r => r.status === 'absent').length
      });
    } catch (error) {
      console.error('Error loading attendance:', error);
      // If no attendance exists, create default records
      const attendanceRecords: AttendanceRecord[] = students.map(student => ({
        studentId: student.studentId,
        studentName: student.name,
        rollNo: student.rollNo ? String(student.rollNo) : '',
        email: student.email || '',
        className: student.className || '',
        status: 'present' as const
      }));

      setAttendanceData({
        date,
        records: attendanceRecords,
        totalPresent: attendanceRecords.filter(r => r.status === 'present').length,
        totalAbsent: attendanceRecords.filter(r => r.status === 'absent').length
      });
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  const handleStatusToggle = (studentId: string) => {
    if (!attendanceData) return;

    setAttendanceData(prev => {
      if (!prev) return null;
      
      const updatedRecords: AttendanceRecord[] = prev.records.map(record => 
        record.studentId === studentId 
          ? { ...record, status: record.status === 'present' ? 'absent' : 'present' }
          : record
      );

      return {
        ...prev,
        records: updatedRecords,
        totalPresent: updatedRecords.filter(r => r.status === 'present').length,
        totalAbsent: updatedRecords.filter(r => r.status === 'absent').length
      };
    });
  };

  const handleSaveAttendance = async () => {
    if (!attendanceData || !user?.schoolId || !user?.classId) return;

    setIsMarkingAttendance(true);
    try {
      const attendanceRecords = attendanceData.records.map(record => ({
        studentId: record.studentId,
        classId: user.classId!,
        markedDate: attendanceData.date,
        isPresent: record.status === 'present',
        schoolId: user.schoolId!
      }));

      const response = await studentAttendanceService.markAttendance(user.schoolId, {
        records: attendanceRecords
      });

      toast.success('Attendance saved successfully!');
      
      // Refresh marked dates
      const markedDatesResponse = await studentAttendanceService.getMarkedDates(user.schoolId);
      setMarkedDates(markedDatesResponse.markedDates);
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance');
    } finally {
      setIsMarkingAttendance(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportFromDate || !reportToDate) {
      toast.error('Please select both from and to dates');
      return;
    }

    const teacherId = authService.getTeacherId();
    if (!user?.schoolId || !user?.classId || !teacherId) {
      toast.error('Missing required information for report generation');
      return;
    }

    setIsGeneratingReport(true);
    try {
      const reportData: DownloadReportRequest = {
        schoolId: user.schoolId,
        classId: user.classId,
        fromDate: reportFromDate,
        toDate: reportToDate,
        teacherId: teacherId
      };

      const blob = await studentAttendanceService.downloadAttendanceReport(reportData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-report-${reportFromDate}-to-${reportToDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully!');
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const formatIndianDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatIndianDateLong = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-IN', options);
  };

  const isAttendanceMarked = (date: string) => {
    return markedDates.includes(date);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Shimmer */}
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
            <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded-lg w-24 animate-pulse"></div>
            <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded-lg w-28 animate-pulse"></div>
          </div>
        </div>

        {/* Date Selection and Stats Shimmer */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <ShimmerCard className="h-24" />
          <ShimmerCard className="h-24" />
        </div>
        <ShimmerCard className="h-32" />

        {/* Table Shimmer */}
        <div className={`rounded-xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-sm`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-40 mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
              </div>
              <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded-lg w-32 animate-pulse"></div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Roll No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {[...Array(3)].map((_, index) => (
                  <ShimmerTableRow key={index} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TeacherPageShell>
      <TeacherPageHeader
        title="Student Attendance"
        description="Mark and manage student attendance"
        action={
          <TeacherHeaderActions>
            <TeacherButton
              variant="secondary"
              icon={Calendar}
              compact
              onClick={() => setShowCalendar(true)}
            >
              <span className="hidden sm:inline">View Calendar</span>
              <span className="sm:hidden">Calendar</span>
            </TeacherButton>
            <TeacherButton
              variant="secondary"
              icon={Download}
              compact
              onClick={() => setShowReportModal(true)}
            >
              <span className="hidden sm:inline">Download Report</span>
              <span className="sm:hidden">Report</span>
            </TeacherButton>
          </TeacherHeaderActions>
        }
      />

      <TeacherStatsGrid cols={2}>
        <TeacherStatCard
          title="Present"
          value={attendanceData?.totalPresent || 0}
          icon={CheckCircle}
          color="emerald"
        />
        <TeacherStatCard
          title="Absent"
          value={attendanceData?.totalAbsent || 0}
          icon={XCircle}
          color="rose"
        />
      </TeacherStatsGrid>

      {/* Date Selection */}
      <TeacherPanel title="Select Date to View Past Attendance">
            <div className="relative">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              />
              <button
                onClick={() => setShowCalendar(true)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            
            <div className="mt-3 space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatIndianDateLong(selectedDate)}
              </p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formatIndianDate(selectedDate)}
              </p>
              
              {/* Attendance Status */}
              <div className="flex items-center space-x-2 mt-3">
                {isAttendanceMarked(selectedDate) ? (
                  <>
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      Attendance Marked
                    </span>
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      Not Marked
                    </span>
                  </>
                )}
              </div>
            </div>
      </TeacherPanel>

      {/* Attendance Table */}
      <div className={`rounded-xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-sm`}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Mark Attendance</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click on the status button to toggle attendance for each student
              </p>
            </div>
            <button
              onClick={handleSaveAttendance}
              disabled={isMarkingAttendance || isLoadingAttendance}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isMarkingAttendance ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Attendance
                </>
              )}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Roll No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoadingAttendance ? (
                [...Array(3)].map((_, index) => (
                  <ShimmerTableRow key={index} />
                ))
              ) : (
                attendanceData?.records.map((record) => (
                  <tr key={record.studentId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {record.rollNo || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                          {record.studentName.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {record.studentName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.className}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleStatusToggle(record.studentId)}
                        className={`px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-2 transition-all duration-200 ${
                          record.status === 'present'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30'
                        }`}
                      >
                        {record.status === 'present' ? (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Present</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Absent</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {attendanceData?.records.length === 0 && !isLoadingAttendance && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No students found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">No students are enrolled in this class.</p>
          </div>
        )}
      </div>

      {/* Custom Calendar Modal */}
      {showCalendar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative">
            <button
              onClick={() => setShowCalendar(false)}
              className="absolute -top-4 -right-4 w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 z-10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <CustomCalendar
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              onClose={() => setShowCalendar(false)}
              isDarkMode={isDarkMode}
              markedDates={markedDates}
            />
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className={`rounded-xl p-6 w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download Attendance Report</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={reportFromDate}
                  onChange={(e) => setReportFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={reportToDate}
                  onChange={(e) => setReportToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDownloadReport}
                  disabled={isGeneratingReport}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isGeneratingReport ? 'Generating...' : 'Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Container */}
      <ToastContainer 
        position="top-right" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </TeacherPageShell>
  );
};

export default StudentAttendance; 